import { IStackProps, Stack } from '@fluentui/react';
import { VirtualItem } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridRow } from '../../../../../../../../../api/generated/data-contracts';
import { ShimmerBox } from '../../../../../../../../../Components/Skeleton/ShimmerBox';
import { useAllRowsClosedContext } from '../../../../../../../_grid/contexts/allRowsClosedContext';
import { useExpandedGridContext } from '../../../../../../../_grid/contexts/expandedContext';
import { ROW_HEIGHT, ROW_BG_HOVER, COLUMN_STICKY_COUNT, TOTAL_COLUMN_WIDTH, COLUMN_WIDTH } from '../../../../CONSTANTS';
import { useGridLayoutContext } from '../../../../context/LayoutContext';
import { TotalColumnContainer } from '../TotalColumnContainer';
import { useTheme } from '../../../../../../../../../Themes/themeContext';
import { TopTotalHeader } from '../../../Headers/TopTotalHeader';
import { TopRowColumnContainer } from './TopRowColumnContainer';
import { preventDefault } from '../../../../../../../../../helpers/killEvent';
import { useGridContext } from '../../../../../../../_grid/contexts/gridContext';
import { useStore } from '../../../../../../../../../context/store';
import { AddRowData } from '../../../AddRowData';
import { changeCase } from '../../../../../../../../../helpers/changeKeysCase';
import { ResourceListItemWithMetadata } from '../../../../../../../_components/panels/types';

export const TopTotalRow = ({
    start,
    totalWidth,
    setTotalReady,
    columnCount,
    columns,
    row,
}: {
    start: number;
    totalWidth: number;
    setTotalReady: (ready: boolean) => void;
    columnCount: number;
    columns: VirtualItem[];
    row?: GridRow;
}) => {
    const { setExpandedRows } = useExpandedGridContext();
    const { rowHeaderSize } = useGridLayoutContext();
    const allRowsClosed = useAllRowsClosedContext();

    const handleExpandClick = useCallback(() => {
        if (allRowsClosed) {
            setExpandedRows(expandedState => Object.fromEntries(Object.entries(expandedState).map(([key]) => [key, true])));
        } else {
            setExpandedRows(expandedState => Object.fromEntries(Object.entries(expandedState).map(([key]) => [key, false])));
        }
    }, [allRowsClosed, setExpandedRows]);

    useEffect(() => {
        if (row) {
            setTotalReady(true);
        }
    }, [row, setTotalReady]);

    const theme = useTheme();
    const styles = useMemo((): IStackProps['styles'] => {
        return {
            root: {
                position: 'absolute',
                transform: `translateY(${start}px)`,
                top: 0,
                left: 0,
                height: `${ROW_HEIGHT}px`,
                width: `${totalWidth}px`,
                boxSizing: 'border-box',
                backgroundColor: theme.semanticColors.bodyBackground,
                '&:hover': {
                    backgroundColor: ROW_BG_HOVER,
                },
            },
        };
    }, [start, theme.semanticColors.bodyBackground, totalWidth]);

    const render = useMemo(() => {
        if (row) {
            return columns.map(({ index, start, key }) => {
                if (index === 0) {
                    return <TopTotalHeader key={String(key)} expanded={!allRowsClosed} toggleExpanded={handleExpandClick} />;
                }
                if (index === 1) {
                    const column = row.totalColumn;
                    return <TotalColumnContainer key={String(key)} column={column} />;
                }
                const columnIndex = index - COLUMN_STICKY_COUNT;
                const column = row.columnRows[columnIndex];
                return <TopRowColumnContainer key={String(key)} columnIndex={columnIndex} columnRow={column} rowId={row.id} start={start} />;
            });
        }
        return [
            <TopTotalHeader key={-2} expanded={!allRowsClosed} toggleExpanded={handleExpandClick} />,
            <Stack
                key={-1}
                verticalAlign="center"
                horizontalAlign="center"
                styles={{ root: { width: TOTAL_COLUMN_WIDTH, position: 'sticky', left: rowHeaderSize } }}
            >
                <ShimmerBox height={ROW_HEIGHT - 10} width={TOTAL_COLUMN_WIDTH - 10} />
            </Stack>,
            ...Array.from({ length: columnCount }, (_, i) => {
                return (
                    <Stack key={i} verticalAlign="center" horizontalAlign="center" styles={{ root: { width: COLUMN_WIDTH } }}>
                        <ShimmerBox height={ROW_HEIGHT - 10} width={COLUMN_WIDTH - 10} />
                    </Stack>
                );
            }),
        ];
    }, [allRowsClosed, columnCount, columns, row, handleExpandClick, rowHeaderSize]);

    const { dropProps } = useDropRow();
    return (
        <Stack styles={styles} horizontal {...dropProps}>
            {render}
        </Stack>
    );
};

const useDropRow = () => {
    const { addRow } = useGridContext<AddRowData>();
    const timelineInfo = useStore(store => store.timeline);
    const [isDraggingOver, setIsDragginOver] = useState(false);
    const [loading, setLoading] = useState(false);
    const { setExpandedRows } = useExpandedGridContext();
    const dropProps = useMemo((): React.DetailedHTMLProps<React.HTMLAttributes<HTMLTableRowElement>, HTMLTableRowElement> => {
        return {
            onDrop: async e => {
                setIsDragginOver(false);
                const resource = getDataFromDragEvent(e);
                if (resource) {
                    setLoading(true);
                    try {
                        const { newRows } = await addRow(null, { resource, timelineInfo, droppedOnTopRow: true }, false);
                        setExpandedRows(state =>
                            newRows.reduce((acc, row) => {
                                return {
                                    ...acc,
                                    [row.id]: true,
                                };
                            }, state),
                        );
                        // console.log('🚀 ~ dropProps ~ newRows, parentRow:', newRows, parentRow);
                    } catch (error) {
                        // console.error('🚀 ~ dropProps ~ error', new Error('Error moving row', { cause: error }));
                        console.error('🚀 ~ dropProps ~ error', error);
                    }
                    setLoading(false);
                }
            },

            // An element with the preventDefault combination on onDragEnter and onDragOver makes it a dropable target.
            // The drag and drop api is retarded https://quirksmode.org/blog/archives/2009/09/the_html5_drag.html
            onDragEnter: e => {
                preventDefault(e);
                setIsDragginOver(true);
            },
            onDragLeave: () => {
                setIsDragginOver(false);
            },
            onDragOver: e => {
                setIsDragginOver(true);
                preventDefault(e);
            },
        };
    }, [addRow, setExpandedRows, timelineInfo]);

    return {
        dropProps,
        isDraggingOver,
        loading,
    };
};

const getDataFromDragEvent = (e: React.DragEvent<HTMLTableRowElement>, dataKey: string = 'resourcelistitem') => {
    const dataString = e.dataTransfer.getData(dataKey);
    if (dataString) {
        const data = changeCase.toCamel(JSON.parse(dataString)) as ResourceListItemWithMetadata;
        return data;
    }
};
